"use client"

import { useState, useContext, useEffect } from "react"
import axios from "axios"
import { useNavigate, Link, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { UserContext } from "../context/UserContext"
import { Eye, EyeOff, LogIn, UserPlus, Lock, Mail, ChevronRight } from "lucide-react"
import "../styles/login.css"

const Login = () => {
  const [email, setEmail] = useState("")
  const [senha, setSenha] = useState("")
  const [erro, setErro] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { loginUser } = useContext(UserContext)
  const [capsLockAtivo, setCapsLockAtivo] = useState(false);


  const from = location.state?.from || "/"

  // Efeito para animação de partículas
  useEffect(() => {
    const canvas = document.getElementById("particles")
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const particles = []
    const particleCount = 50

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        color: "rgba(255, 193, 7, " + (Math.random() * 0.3 + 0.2) + ")",
        speedX: Math.random() * 0.5 - 0.25,
        speedY: Math.random() * 0.5 - 0.25,
      })
    }

    function animate() {
      requestAnimationFrame(animate)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (let i = 0; i < particleCount; i++) {
        const p = particles[i]
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.fill()

        p.x += p.speedX
        p.y += p.speedY

        if (p.x < 0 || p.x > canvas.width) p.speedX *= -1
        if (p.y < 0 || p.y > canvas.height) p.speedY *= -1
      }
    }

    animate()

    const handleResize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    setErro("")
    setIsLoading(true)

    try {
      const response = await axios.post("https://api.ticketdovaqueiro.com/api/auth/login", {
        email,
        senha,
      })

      const { token, user } = response.data
      loginUser(user, token) // Atualiza contexto + localstorage

      if (user.tipo === "admin") {
        navigate("/admin/dashboard")
      } else if (user.tipo === "dono") {
        navigate("/dono/dashboard")
      } else {
        navigate(from) // Redireciona para a página de origem
      }
    } catch (error) {
      const msg = error.response?.data?.mensagem || "E-mail ou senha inválidos!";
      setErro(msg);

    } finally {
      setIsLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="login-page44">
      <canvas id="particles" className="particles-canvas"></canvas>

      <div className="login-overlay44">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="login-container44"
        >
          <div className="login-content44">
            <div className="login-section44">
              <motion.div
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="login-form-container44"
              >
                <h2 className="login-title44">
                  <LogIn className="login-icon44" />
                  Acesse sua conta
                </h2>

                <AnimatePresence>
                  {erro && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="login-error44"
                    >
                      {erro}
                    </motion.div>
                  )}
                </AnimatePresence>

                <form onSubmit={handleLogin} className="login-form44">
                  <div className="form-group44">
                    <label htmlFor="email">E-mail</label>
                    <div className="input-wrapper44">
                      <Mail className="input-icon44" />
                      <input
                        id="email"
                        type="email"
                        placeholder="Seu e-mail"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        onKeyUp={(e) => setCapsLockAtivo(e.getModifierState("CapsLock"))}
                      />
                    </div>
                    {capsLockAtivo && (
                      <span style={{ color: "orange", fontSize: "12px", marginTop: "4px", display: "block" }}>
                        ⚠️ CAPS LOCK está ativado
                      </span>
                    )}
                  </div>

                  <div className="form-group44">
                    <label htmlFor="password">Senha</label>
                    <div className="input-wrapper44">
                      <Lock className="input-icon44" />
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Sua senha"
                        value={senha}
                        onChange={(e) => setSenha(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle44"
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="forgot-password721">
                    <Link to="/recuperar-senha">Esqueceu a Senha?</Link>
                  </div>
                  
                  <div className="form-actions44">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      type="submit"
                      className="login-button44"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="button-loader44"></div>
                      ) : (
                        <>
                          ENTRAR
                          <ChevronRight size={18} />
                        </>
                      )}
                    </motion.button>
                                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/cadastro"
                  state={{ from }}
                  className="register-button44"
                >
                  CRIAR CONTA
                  <ChevronRight size={18} />
                </Link>

                </motion.div>
                    <div className="login-links-row" style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "10px"
                    }}>

                    </div>
                  </div>

                </form>
              </motion.div>
            </div>

            <div className="separator44">
              <div className="separator-line44"></div>
              <span>ou</span>
              <div className="separator-line44"></div>
            </div>

            <div className="register-section44">
              <motion.div
                initial={{ x: 30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="register-content44"
              >
                <h2 className="register-title44">
                  <UserPlus className="register-icon44" />
                  Novo por aqui?
                </h2>

                <p className="register-description44">
                  Cadastre-se agora e participe das melhores vaquejadas do Brasil!
                </p>

                <div className="register-benefits44">
                  <div className="benefit-item44">
                    <div className="benefit-icon44">✓</div>
                    <span>Inscrições rápidas e seguras</span>
                  </div>
                  <div className="benefit-item44">
                    <div className="benefit-icon44">✓</div>
                    <span>Facilidade no pagamento</span>
                  </div>
                  <div className="benefit-item44">
                    <div className="benefit-icon44">✓</div>
                    <span>Receba notificações de eventos</span>
                  </div>
                </div>

                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/cadastro"
                  state={{ from }}
                  className="register-button44"
                >
                  CRIAR CONTA
                  <ChevronRight size={18} />
                </Link>

                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Login
